import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { AppConfig, CMPS_APP_CONFIG } from '../../../app.config';
import { AuthService } from '../../../auth/auth.service';
import { mapRetrieve } from '../../../common/rest.operators';
import { User } from '../../../user/user.model';
import { UserService } from '../../../user/user.service';
import { PeriodType } from '../../../widget/date-range.widget.model';
import { logoutWatcher } from '../../logout-watcher.function';
import {
    CoachingComponentsTransformer,
    CoachingInsightComponent,
    CoachingInsightComponentAttributes,
    CoachingInsightConfig,
    CoachingInsightConfigAttribute,
    CoachingInsightSubComponent,
    CoachingSummaryResponse,
    ColumnConfigItem,
} from '../models/coaching.global.model';

import { FrontlineOAService } from './frontline-oa.service';
import { TeamOAService } from './team-oa.service';

// id of the metric id that toggles the team OA table
const OPPORTUNITYALIGNMENT_SHORTNAME = 'OA';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OPPORTUNITYALIGNMENT_ID = 0;

@Injectable()
export class CoachingInsightWidgetService {
    private _coachingComponents: BehaviorSubject<CoachingInsightComponent[]>;
    private readonly _user: Observable<User>;
    private _coachingView: BehaviorSubject<string | number>;
    private _config: BehaviorSubject<CoachingInsightConfig>;
    public _periodTypeId: BehaviorSubject<string> = new BehaviorSubject<string>(
        null,
    ); // PeriodType = null;

    public componentConfig: CoachingInsightComponentAttributes;

    constructor(
        private readonly _http: HttpClient,
        private readonly _router: Router,
        private readonly _authService: AuthService,
        private readonly _userService: UserService,
        private readonly _teamOAService: TeamOAService,
        private readonly _floaService: FrontlineOAService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        this._user = this._userService.retrieve();

        logoutWatcher(this._router, this._authService);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resetWidget(includeConfig?: boolean) {
        // this._coachingComponents.next(null);
        // this._coachingView.next(null);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._coachingComponents && this._coachingComponents.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._coachingView && this._coachingView.complete();

        if (includeConfig) {
            // this._config.next(null);
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._periodTypeId && this._periodTypeId.complete();
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._config && this._config.complete();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resetComponents() {
        // this._coachingComponents.next(null);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._coachingComponents && this._coachingComponents.complete();
    }

    get coachingComponents(): Observable<CoachingInsightComponent[]> {
        return this._coachingComponents.asObservable();
    }

    get coachingView(): Observable<string | number> {
        return this._coachingView.asObservable();
    }

    get config(): Observable<any> {
        return this._config.asObservable();
    }

    get user(): Observable<User> {
        return this._user;
    }

    get periodTypeId(): Observable<string> {
        return this._periodTypeId.asObservable();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initializeSubjects() {
        this._coachingComponents = new BehaviorSubject<
            CoachingInsightComponent[]
        >(null);
        this._coachingView = new BehaviorSubject<string | number>(null);
        this._config = new BehaviorSubject<CoachingInsightConfig>(null);
        this._periodTypeId = new BehaviorSubject<string>(null); // PeriodType = null;
    }

    getPeriodTypeId(): string {
        return this._periodTypeId.getValue();
    }

    getPeriodTypeEnumId(): PeriodType {
        const id = parseInt(this.getPeriodTypeId());
        const pds = [
            PeriodType.DAY,
            PeriodType.MONTH,
            PeriodType.ROLLING_30,
            PeriodType.WEEK,
        ];
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        return pds.indexOf(id) !== -1 ? (id as PeriodType) : PeriodType.MONTH;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setPeriodTypeId(val: string) {
        this._periodTypeId.next(val);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setCoachingViewName(val: string) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._coachingView && this._coachingView.next(val);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setConfigAttributes(attrs: CoachingInsightConfigAttribute[]) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        this.componentConfig = <CoachingInsightComponentAttributes>{};

        if (attrs) {
            attrs.forEach((a) => {
                // handle dd-mm-yyyy
                if (a.attributeName === 'lastUpdated') {
                    let testdate = a.attributeValue;
                    let spl = testdate.split('-');
                    if (spl.length === 3 && spl[2].length === 4) {
                        spl = [spl[2], spl[1], spl[0]];
                        testdate = spl.join('-');
                    }

                    this.componentConfig[a.attributeName] = new Date(testdate);
                } else if (a.attributeName === 'defaultExpandWidget') {
                    this.componentConfig[a.attributeName] =
                        typeof a.attributeValue === 'string'
                            ? a.attributeValue === 'true'
                            : a.attributeValue;
                } else this.componentConfig[a.attributeName] = a.attributeValue;
            });
        }

        return this.componentConfig;
    }

    getComponentAttributes(
        componentTypeId?: number,
    ): CoachingInsightComponentAttributes {
        return this.componentConfig;
    }

    getOAToggle(): Observable<boolean> {
        const components = this._coachingComponents.getValue();
        const o = components.find((metric) => {
            return metric.componentShortName === OPPORTUNITYALIGNMENT_SHORTNAME;
        });

        return o.toggle.asObservable();
    }

    retrieveCoachingInsightConfig(): void {
        this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/coaching-insight/config`,
            )
            .pipe(mapRetrieve())
            .subscribe(
                (data) => this._mapConfigResponse(data),
                (error) => {
                    console.error(
                        'Cannot retrieve Coaching Insight Config.',
                        error,
                    );
                    this._config.error(error);
                },
            );
    }

    retrieveCoachingComponents(performerUserId: any, periodTypeId: any): void {
        let params = new HttpParams().set('periodTypeId', periodTypeId);
        if (performerUserId != null) {
            params = params.set('performerUserId', performerUserId);
        }

        this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/coaching-insight/summary`,
                { params },
            )
            .pipe(mapRetrieve())
            // must call _mapComponentsResponse like this, otherwise BehaviorSubject won't next()
            .subscribe(
                (data) => this._mapComponentsResponse(data),
                (error) => console.error('Cannot retrieve OA Metrics.', error),
            );
    }

    // this change to 1 metric propagates up to the last value of oametrics$ Subject, which is an array of metrics
    toggleComponent(component: any, force?: boolean): void {
        document.querySelector('body').style.overflowX = 'hidden';
        const comps = this._coachingComponents.getValue();

        if (typeof component === 'number') {
            const x = comps.filter((d) => d.componentTypeId === component);
            if (x.length) {
                component = x[0];
            }
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        } else component = <CoachingInsightComponent>component;

        let activecomponentindex = null;

        comps.forEach((i, idx) => {
            if (i.componentTypeId !== component.componentTypeId) {
                i.iconstate = false;
                i.toggle.next(i.iconstate);
            } else {
                activecomponentindex = idx;
            }
        });

        setTimeout(() => {
            comps[activecomponentindex].iconstate =
                force !== null ? force : !comps[activecomponentindex].iconstate;
            comps[activecomponentindex].toggle.next(
                comps[activecomponentindex].iconstate,
            );

            this.setCoachingViewName(
                comps[activecomponentindex].iconstate === true
                    ? comps[activecomponentindex].componentInitials
                    : null,
            );

            // eslint-disable-next-line no-return-assign
            setTimeout(
                () => (document.querySelector('body').style.overflowX = 'auto'),
                1,
            );
            this._teamOAService.updateActiveColumns();
            this._coachingComponents.next(comps);
        }, 100);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleSubmenu(subcomponent: CoachingInsightSubComponent, id: any) {
        if (subcomponent.subComponentTypeId === id) {
            subcomponent.active = !subcomponent.active;
            setTimeout(() => {
                this._teamOAService.updateActiveColumns();
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    updateSubmenu(id: number) {
        const c = this._coachingComponents.getValue();
        c.forEach((g) => {
            g.coachingInsightSubComponents.forEach((s) => {
                if (s.subComponentTypeId === id) {
                    this.toggleSubmenu(s, id);
                }
            });
        });
        this._coachingComponents.next(c);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resetSubmenu(cols: ColumnConfigItem[]) {
        const c = this._coachingComponents.getValue();
        c.forEach((g) => {
            g.coachingInsightSubComponents.forEach((s) => {
                const conf = cols.find(
                    (h) => h.bindToId === s.subComponentTypeId,
                );

                if (conf) {
                    s.active = conf.active;
                }
            });
        });
        this._coachingComponents.next(c);
    }

    private _mapComponentsResponse(data: CoachingSummaryResponse): void {
        const components =
            new CoachingComponentsTransformer().transform(data) || [];

        // this._teamOAService.initializeColumnConfig();
        this._floaService.initializeColumnConfig();

        // initially set component icon state observables to nothing
        components.forEach((component: CoachingInsightComponent) => {
            component.toggle = new BehaviorSubject<boolean>(false);
        });

        let defaultactivecomponent = components.find(
            (el: CoachingInsightComponent) =>
                el.componentShortName === OPPORTUNITYALIGNMENT_SHORTNAME,
        );

        if (!defaultactivecomponent) {
            defaultactivecomponent = components[0];
        }

        defaultactivecomponent.iconstate = false;
        defaultactivecomponent.toggle.next(defaultactivecomponent.iconstate); // initial toggle

        // initial values
        this._coachingComponents.next(components);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _mapConfigResponse(data: CoachingInsightConfig) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (data && data.availablePeriodTypes) {
            const x = data.availablePeriodTypes.find(
                (d) => d.isDefault === true,
            );
            this._periodTypeId.next(x ? x.periodTypeId.toString() : '4');
        }
        this._config.next(data);
    }
}
